<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  show: Boolean,
  postData: Object
})

const emit = defineEmits(['update:show'])

// 图片预览
const showImagePreview = ref(false)
const previewImageUrl = ref('')

const handleClose = () => {
  emit('update:show', false)
}

const imageList = computed(() => {
  if (!props.postData?.images) return []
  return props.postData.images.split(',').filter(url => url.trim())
})

const formatTime = (time) => {
  if (!time) return '-'
  return time.replace('T', ' ').substring(0, 16)
}

const sourceLabel = computed(() => {
  if (!props.postData) return '-'
  return props.postData.source === 0 ? 'C端用户' : '平台'
})

const contentTypeLabel = computed(() => {
  if (!props.postData) return '-'
  return props.postData.contentType === 1 ? '图文' : '短视频'
})

const statusConfig = {
  0: { label: '待审核', dot: 'bg-amber-400', bg: 'bg-amber-50', text: 'text-amber-700' },
  1: { label: '已通过', dot: 'bg-green-500', bg: 'bg-green-50', text: 'text-green-700' },
  2: { label: '已拒绝', dot: 'bg-red-400', bg: 'bg-red-50', text: 'text-red-700' },
  3: { label: '已下架', dot: 'bg-slate-400', bg: 'bg-slate-100', text: 'text-slate-700' }
}

const openImagePreview = (url) => {
  previewImageUrl.value = url
  showImagePreview.value = true
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="handleClose"></div>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <!-- 头部 -->
            <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
              <h3 class="text-lg font-semibold text-slate-900">动态详情</h3>
              <button
                @click="handleClose"
                class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- 内容 -->
            <div v-if="postData" class="flex-1 overflow-y-auto p-6 space-y-6">
              <!-- 发布者信息 -->
              <div class="flex items-center gap-3">
                <img
                  v-if="postData.userAvatar"
                  :src="postData.userAvatar"
                  class="w-10 h-10 rounded-full object-cover border border-slate-200"
                />
                <div v-else class="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-slate-900">{{ postData.userNickname || '未知用户' }}</div>
                  <div class="text-xs text-slate-500">{{ formatTime(postData.createTime) }}</div>
                </div>
                <span
                  class="status-badge"
                  :class="[statusConfig[postData.status]?.bg, statusConfig[postData.status]?.text]"
                >
                  <span class="w-1.5 h-1.5 rounded-full mr-1.5" :class="statusConfig[postData.status]?.dot"></span>
                  {{ statusConfig[postData.status]?.label || '未知' }}
                </span>
              </div>

              <!-- 基本信息标签 -->
              <div class="flex items-center gap-2 flex-wrap">
                <span class="status-badge bg-blue-50 text-blue-700">
                  {{ sourceLabel }}
                </span>
                <span class="status-badge bg-purple-50 text-purple-700">
                  {{ contentTypeLabel }}
                </span>
                <span v-if="postData.isTop === 1" class="status-badge bg-orange-50 text-orange-700">
                  置顶
                </span>
              </div>

              <!-- 文字内容 -->
              <div v-if="postData.content">
                <h4 class="text-sm font-semibold text-slate-900 mb-2">动态内容</h4>
                <p class="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{{ postData.content }}</p>
              </div>

              <!-- 图片列表 -->
              <div v-if="imageList.length > 0">
                <h4 class="text-sm font-semibold text-slate-900 mb-2">图片</h4>
                <div class="grid grid-cols-3 gap-2">
                  <div
                    v-for="(url, index) in imageList"
                    :key="index"
                    class="relative aspect-square rounded-lg overflow-hidden border border-slate-200 cursor-pointer group"
                    @click="openImagePreview(url)"
                  >
                    <img :src="url" class="w-full h-full object-cover" />
                    <div class="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <svg class="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 视频 -->
              <div v-if="postData.contentType === 2 && postData.videoUrl">
                <h4 class="text-sm font-semibold text-slate-900 mb-2">视频</h4>
                <div class="rounded-lg overflow-hidden border border-slate-200">
                  <video
                    :src="postData.videoUrl"
                    :poster="postData.videoCover"
                    controls
                    class="w-full max-h-80"
                  ></video>
                </div>
              </div>

              <!-- 跳转链接 -->
              <div v-if="postData.linkUrl">
                <h4 class="text-sm font-semibold text-slate-900 mb-2">跳转链接</h4>
                <div class="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div class="text-sm text-slate-700">{{ postData.linkTitle || '无标题' }}</div>
                  <div class="text-xs text-blue-600 mt-1 break-all">{{ postData.linkUrl }}</div>
                </div>
              </div>

              <!-- 互动数据 -->
              <div>
                <h4 class="text-sm font-semibold text-slate-900 mb-2">互动数据</h4>
                <div class="grid grid-cols-3 gap-3">
                  <div class="bg-slate-50 rounded-lg p-3 text-center border border-slate-200">
                    <div class="text-lg font-semibold text-slate-900">{{ postData.viewCount || 0 }}</div>
                    <div class="text-xs text-slate-500 mt-0.5">浏览量</div>
                  </div>
                  <div class="bg-slate-50 rounded-lg p-3 text-center border border-slate-200">
                    <div class="text-lg font-semibold text-slate-900">{{ postData.likeCount || 0 }}</div>
                    <div class="text-xs text-slate-500 mt-0.5">点赞数</div>
                  </div>
                  <div class="bg-slate-50 rounded-lg p-3 text-center border border-slate-200">
                    <div class="text-lg font-semibold text-slate-900">{{ postData.favoriteCount || 0 }}</div>
                    <div class="text-xs text-slate-500 mt-0.5">收藏数</div>
                  </div>
                </div>
              </div>

              <!-- 置顶信息 -->
              <div v-if="postData.isTop === 1">
                <h4 class="text-sm font-semibold text-slate-900 mb-2">置顶信息</h4>
                <div class="bg-orange-50 rounded-lg p-3 border border-orange-200 text-sm">
                  <div class="flex items-center gap-4">
                    <span class="text-slate-600">排序值：<span class="text-slate-900 font-medium">{{ postData.topSort || 0 }}</span></span>
                    <span class="text-slate-600">过期时间：<span class="text-slate-900 font-medium">{{ formatTime(postData.topExpireTime) || '永不过期' }}</span></span>
                  </div>
                </div>
              </div>

              <!-- 审核信息 -->
              <div v-if="postData.status === 2 && postData.rejectReason">
                <h4 class="text-sm font-semibold text-slate-900 mb-2">拒绝原因</h4>
                <div class="bg-red-50 rounded-lg p-3 border border-red-200">
                  <p class="text-sm text-red-700">{{ postData.rejectReason }}</p>
                </div>
              </div>

              <div v-if="postData.auditTime">
                <h4 class="text-sm font-semibold text-slate-900 mb-2">审核信息</h4>
                <div class="text-sm text-slate-600">
                  审核时间：{{ formatTime(postData.auditTime) }}
                </div>
              </div>
            </div>

            <!-- 底部 -->
            <div class="px-6 py-4 border-t border-slate-200 flex items-center justify-end shrink-0">
              <button
                @click="handleClose"
                class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors cursor-pointer"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 大图预览 -->
    <Transition name="modal">
      <div v-if="showImagePreview" class="fixed inset-0 z-[60] flex items-center justify-center" @click.self="showImagePreview = false">
        <div class="absolute inset-0 bg-black/70" @click="showImagePreview = false"></div>
        <div class="relative max-w-[90vw] max-h-[90vh]">
          <img :src="previewImageUrl" alt="大图预览" class="max-w-full max-h-[85vh] rounded-lg object-contain" />
          <button
            @click="showImagePreview = false"
            class="absolute -top-3 -right-3 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-lg text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
